import './FeatureMappingTable.scss';
import cn from 'classnames';
import { Checkbox } from '@Components/FormCheck';
import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button } from '../../../Components/Button/Button';
import { Appearance, Design, Size } from '../../../Components/designConstants';
import { Dropdown } from '../../../Components/Dropdown';
import { DropdownVariant } from '../../../Components/Dropdown/dropdown-constants';
import { FrozenColumnsTable } from '../../../Components/FrozenColumnsTable/FrozenColumnsTable';
import { Header } from '../../../Components/StickyTable/BPMTable';
import { CellRenderer, CornerLocation } from '../../../Components/StickyTable/StickyTable';
import { useMap } from '../../../utils/hooks/useData';

export interface SegmentValue {
  value_id: number | string;
  value_name: string;
}

export type SegmentValues = Record<string, SegmentValue>;

export type MappingRow = SegmentValues & {
  comboKey: string;
  edits?: SegmentValues;
  pctSpend?: number;
};

export type EditsMap = Record<string, MappingRow>;

export interface EditsMapAction {
  type: 'UPDATE_SEGMENT_EDIT' | 'DELETE_SEGMENT_EDIT';
  updates?: { rows: MappingRow[]; edit: SegmentValues }[];
  comboKeys?: string[];
  segmentName?: string;
}

export interface OutputFeatureData {
  segment_id: number;
  segment_name: string;
  segment_values: SegmentValue[];
}

const getSortValue = (val: unknown): string => {
  if (typeof val === 'string') return val;
  if (val && typeof val === 'object' && 'value_name' in val) {
    return String((val as SegmentValue).value_name ?? '');
  }
  return '';
};

const compareSegmentValues = (a: SegmentValue, b: SegmentValue) =>
  a.value_name.localeCompare(b.value_name, undefined, { sensitivity: 'base' });

const CHECKBOX_WIDTH = 28;
const SEGMENT_WIDTH = 120;
const TOP_HEIGHT = 60; // header row height passed to FrozenColumnsTable
const ROW_HEIGHT = 50; // StickyTable default row height

interface CustomValueOptionProps {
  label: string;
  onDelete: () => void;
}

const CustomValueOption = ({ label, onDelete }: CustomValueOptionProps) => (
  <div className='fm-custom-option'>
    <span>{label}</span>
    <Button
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      disableAnimation
      className='fm-custom-option-delete'
      aria-label={`Remove ${label}`}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onDelete();
      }}
    >
      ✕
    </Button>
  </div>
);

// Defined at module level so ESLint's no-unstable-nested-components rule is satisfied.
// Returns a formatOptionLabel function scoped to one output segment column.
const makeFormatOptionLabel = (
  originalValueIds: Set<string>,
  segmentName: string,
  getOptions: () => SegmentValue[],
  setOptions: (values: SegmentValue[]) => void,
  editsMap: EditsMap,
  dispatch: Dispatch<EditsMapAction>
) =>
  function formatOptionLabel(
    { label, value }: { label: string; value: string },
    meta?: { context: 'menu' | 'value' }
  ) {
    if (meta?.context === 'value' || originalValueIds.has(value)) {
      return <span>{label}</span>;
    }
    return (
      <CustomValueOption
        label={label}
        onDelete={() => {
          setOptions(getOptions().filter(sv => String(sv.value_id) !== value));
          const affectedKeys = Object.keys(editsMap).filter(key => {
            const { edits } = editsMap[key];
            return String(edits?.[segmentName]?.value_id) === value;
          });
          if (affectedKeys.length > 0) {
            dispatch({ type: 'DELETE_SEGMENT_EDIT', comboKeys: affectedKeys, segmentName });
          }
        }}
      />
    );
  };

interface FeatureMappingTableProps {
  data: MappingRow[];
  inputSegments: string[];
  outputSegments: OutputFeatureData[] | undefined;
  editsMap: EditsMap;
  selectedRows: Record<string, MappingRow>;
  dispatchEditsMap: Dispatch<EditsMapAction>;
  setSelectedRows: Dispatch<SetStateAction<Record<string, MappingRow>>>;
}

export const FeatureMappingTable = ({
  data,
  inputSegments,
  outputSegments,
  editsMap,
  selectedRows,
  dispatchEditsMap,
  setSelectedRows
}: FeatureMappingTableProps) => {
  const [selectAll, setSelectAll] = useState(false);
  const [resizeActive, setResizeActive] = useState(false);
  const [resizedLeftWidth, setResizedLeftWidth] = useState<number | undefined>();
  const [nearDivider, setNearDivider] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const selectedRowsSet = useMemo(() => new Set(Object.keys(selectedRows)), [selectedRows]);

  const selectRow = useCallback(
    (row: MappingRow) => {
      const { comboKey } = row;
      if (comboKey in selectedRows) {
        setSelectedRows(current => {
          const next = { ...current };
          delete next[comboKey];
          return next;
        });
      } else {
        setSelectedRows(current => ({ ...current, [comboKey]: row }));
      }
    },
    [selectedRows, setSelectedRows]
  );

  const dataWithEdits = useMemo(
    () =>
      data.map(row => {
        if (editsMap[row.comboKey]) {
          return editsMap[row.comboKey];
        }
        return row;
      }),
    [editsMap, data]
  );

  const dataMap = useMemo(() => {
    const output: Record<string, MappingRow> = {};
    data.forEach(row => {
      output[row.comboKey] = row;
    });
    return output;
  }, [data]);

  const handleInputChange = useCallback(
    (
      selection: { label: string; value: string | number } | null,
      comboKey: string,
      segment: OutputFeatureData,
      rowToUse: MappingRow
    ) => {
      const keys = selectedRowsSet.has(comboKey) ? Array.from(selectedRowsSet) : [comboKey];
      const rows = selectedRowsSet.has(comboKey) ? Object.values(selectedRows) : [rowToUse];

      if (!selection) {
        dispatchEditsMap({
          type: 'DELETE_SEGMENT_EDIT',
          segmentName: segment.segment_name,
          comboKeys: keys
        });
        return;
      }

      dispatchEditsMap({
        type: 'UPDATE_SEGMENT_EDIT',
        updates: [
          {
            rows,
            edit: {
              [segment.segment_name]: { value_id: selection.value, value_name: selection.label }
            }
          }
        ]
      });
    },
    [dispatchEditsMap, selectedRows, selectedRowsSet]
  );

  const [segmentValueOptions, setSegmentValueOptionsValue] = useMap<string, SegmentValue[]>(
    outputSegments?.reduce((acc, segment) => {
      acc[segment.segment_name] = [...segment.segment_values].sort(compareSegmentValues);
      return acc;
    }, {})
  );

  const rightTableHeaders: Header[] = useMemo(
    () =>
      (outputSegments || []).map(segment => {
        const { segment_id, segment_name } = segment;
        const originalValueIds = new Set(segment.segment_values.map(sv => String(sv.value_id)));

        return {
          label: segment_name,
          name: segment_name,
          width: 200,
          flex: 1,
          renderer: (row: MappingRow) => {
            if (!row) return <div />;

            const rowToUse = editsMap[row.comboKey] || row;
            const { comboKey } = rowToUse;

            const val =
              segment_name in (rowToUse.edits ?? {})
                ? rowToUse.edits?.[segment_name]
                : rowToUse[segment_name];

            const cellOptions = (segmentValueOptions[segment_name] ?? []).map(sv => ({
              label: sv.value_name,
              value: String(sv.value_id)
            }));
            const cellValue = val ? String(val.value_id) : null;

            const isCreatable = segment_id !== 4; // Don't allow write-in values for Brand / Non-Brand
            return (
              <Dropdown
                size={Size.LG}
                searchable
                clearable={segment_name in (rowToUse.edits ?? {})}
                creatable={isCreatable}
                variant={DropdownVariant.CELL}
                options={cellOptions}
                value={cellValue}
                placeholder='Select/create value'
                formatOptionLabel={makeFormatOptionLabel(
                  originalValueIds,
                  segment_name,
                  () => segmentValueOptions[segment_name] ?? [],
                  values => setSegmentValueOptionsValue(segment_name, values),
                  editsMap,
                  dispatchEditsMap
                )}
                onChange={selection => {
                  if (!selection) {
                    handleInputChange(null, comboKey, segment, rowToUse);
                    return;
                  }
                  // Look up the original SegmentValue so we preserve the
                  // numeric value_id for existing values. The dropdown only
                  // carries stringified ids; passing those back to save would
                  // make the server treat "42" as a new value named "42".
                  const matched = (segmentValueOptions[segment_name] ?? []).find(
                    sv => String(sv.value_id) === selection
                  );
                  if (!matched) return;
                  handleInputChange(
                    { label: matched.value_name, value: matched.value_id },
                    comboKey,
                    segment,
                    rowToUse
                  );
                }}
                reactSelectProps={{
                  onCreateOption: isCreatable
                    ? option => {
                        const existing = segmentValueOptions[segment_name] || [];
                        if (!existing.some(v => v.value_name === option)) {
                          setSegmentValueOptionsValue(
                            segment_name,
                            [...existing, { value_id: option, value_name: option }].sort(
                              compareSegmentValues
                            )
                          );
                        }
                        handleInputChange(
                          { label: option, value: option },
                          comboKey,
                          segment,
                          rowToUse
                        );
                      }
                    : undefined,
                  formatCreateLabel: isCreatable ? inputVal => `New value: ${inputVal}` : undefined
                }}
              />
            );
          }
        };
      }),
    [
      outputSegments,
      editsMap,
      segmentValueOptions,
      setSegmentValueOptionsValue,
      handleInputChange,
      dispatchEditsMap
    ]
  );

  // Making this its own thing to prevent rerenders that happened when using rightTableHeaders.
  const rightHeaderNames = useMemo(
    () => (rightTableHeaders || []).map(header => header.name || ''),
    [rightTableHeaders]
  );

  const leftHeaderNames = useMemo(
    () => inputSegments.filter(segment => segment !== 'comboKey'),
    [inputSegments]
  );

  const customLeftRenderer: CellRenderer<Record<string, unknown>> = useCallback(
    ({ data: rowData, style = {}, classes = [] }) => (
      <LeftCell
        rowData={rowData}
        style={style}
        classes={classes}
        selectedRows={selectedRows}
        leftHeaderNames={leftHeaderNames}
        onSelectRow={comboKey => selectRow(dataMap[comboKey])}
      />
    ),
    [dataMap, leftHeaderNames, selectRow, selectedRows]
  );

  const [rightData, setRightData] = useState<{ originalRowIndex: number; content: unknown }[][]>(
    []
  );

  const sortRightData = useCallback(
    (columnName: string, direction: string) => {
      const tempData = [...dataWithEdits];

      if (direction === 'up') {
        tempData.sort((a, b) =>
          getSortValue(a[columnName]).localeCompare(getSortValue(b[columnName]))
        );
      }
      if (direction === 'down') {
        tempData.sort((a, b) =>
          getSortValue(b[columnName]).localeCompare(getSortValue(a[columnName]))
        );
      }

      const originalIndexMap: Record<string, number> = {};
      for (let i = 0; i < dataWithEdits.length; i++) {
        originalIndexMap[dataWithEdits[i].comboKey] = i;
      }

      const sorted: { originalRowIndex: number; content: unknown }[][] = [];
      for (let i = 0; i < tempData.length; ++i) {
        const obj = tempData[i];
        const row: { originalRowIndex: number; content: unknown }[] = [];
        for (const { name } of rightTableHeaders) {
          const content = obj[name ?? 'undefined'];
          row.push({
            originalRowIndex: originalIndexMap[obj.comboKey],
            content: content ?? ''
          });
        }
        sorted.push(row);
      }
      setRightData(sorted);
    },
    [dataWithEdits, rightTableHeaders]
  );

  const [sortMap, setSortMapValue, setSortMap] = useMap<string, string>({});

  const updateSortingMap = useMemo(() => ({ off: 'up', up: 'down', down: 'off' }), []);

  const onSort = useCallback(
    (propertyName: string, event: React.MouseEvent) => {
      // Prevent sorting when clicking on the name/id toggle
      if (event.target instanceof HTMLElement) {
        const { classList } = event.target;
        if (classList.contains('bf-label') || classList.contains('toggle-item')) {
          return;
        }
      }
      setSortMap({});
      setSortMapValue(propertyName, updateSortingMap[sortMap[propertyName] || 'off']);
      sortRightData(propertyName, updateSortingMap[sortMap[propertyName] || 'off']);
    },
    [setSortMap, setSortMapValue, sortMap, sortRightData, updateSortingMap]
  );

  const customCornerRenderer = useCallback(
    (corner: CornerLocation) => {
      if (corner !== 'nw') return <div />;

      return (
        <NWCornerCell
          selectAll={selectAll}
          leftHeaderNames={leftHeaderNames}
          sortMap={sortMap}
          data={data}
          setSelectedRows={setSelectedRows}
          setSelectAll={setSelectAll}
          onSort={onSort}
        />
      );
    },
    [selectAll, leftHeaderNames, setSelectedRows, data, sortMap, onSort]
  );

  const noRowsRenderer = useCallback(
    () => (data.length === 0 ? <div className='no-rows'>No rows to display.</div> : <div />),
    [data.length]
  );

  const startingWidth = useMemo(
    () => Math.max(100, CHECKBOX_WIDTH + leftHeaderNames.length * SEGMENT_WIDTH),
    [leftHeaderNames]
  );

  const leftWidth = resizedLeftWidth ?? startingWidth;

  const handleResizeMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!tableRef.current) return;
      const {
        left: tableLeft,
        top: tableTop,
        width: tableWidth
      } = tableRef.current.getBoundingClientRect();
      const mouseX = e.clientX - tableLeft;
      if (resizeActive) {
        const MIN_PANEL_WIDTH = 100;
        setResizedLeftWidth(
          Math.max(MIN_PANEL_WIDTH, Math.min(tableWidth - MIN_PANEL_WIDTH, mouseX))
        );
      } else {
        const dataAreaBottom = tableTop + TOP_HEIGHT + data.length * ROW_HEIGHT;
        setNearDivider(e.clientY <= dataAreaBottom && Math.abs(mouseX - leftWidth) <= 10);
      }
    },
    [resizeActive, leftWidth, data.length]
  );

  const isNearOrDragging = nearDivider || resizeActive;

  return (
    <div
      ref={tableRef}
      className={cn('feature-mapping-table', {
        'near-divider': isNearOrDragging,
        dragging: resizeActive
      })}
      style={
        {
          '--resize-left': `${Math.max(0, leftWidth - 10)}px`,
          '--resize-height': `${TOP_HEIGHT + data.length * ROW_HEIGHT}px`
        } as CSSProperties
      }
      role='presentation'
      onMouseMove={handleResizeMouseMove}
      onMouseUp={() => setResizeActive(false)}
      onMouseLeave={() => {
        setNearDivider(false);
        setResizeActive(false);
      }}
    >
      <div className='resize-container'>
        <button
          type='button'
          className='resize-area'
          aria-label='Resize columns'
          onMouseDown={e => {
            if (!tableRef.current) return;
            const { top: tableTop } = tableRef.current.getBoundingClientRect();
            if (e.clientY > tableTop + TOP_HEIGHT + data.length * ROW_HEIGHT) return;
            setResizeActive(true);
          }}
        />
      </div>
      <div className='table-content'>
        <FrozenColumnsTable
          leftWidth={leftWidth}
          leftRenderer={customLeftRenderer}
          cornerRenderer={customCornerRenderer}
          rightTableHeaders={rightTableHeaders}
          rightTableHeaderNames={rightHeaderNames}
          originalRightData={data}
          leftDataFields={inputSegments}
          rightData={rightData}
          setRightData={setRightData}
          noRowsRenderer={noRowsRenderer}
          fillContainer
          topHeight={60}
        />
      </div>
    </div>
  );
};

// — Private sub-components —

interface LeftCellProps {
  rowData: Record<string, unknown>;
  style: object;
  classes: string[];
  selectedRows: Record<string, MappingRow>;
  leftHeaderNames: string[];
  onSelectRow: (comboKey: string) => void;
}

const LeftCell = ({
  rowData,
  style,
  classes,
  selectedRows,
  leftHeaderNames,
  onSelectRow
}: LeftCellProps) => {
  const comboKey = String(rowData.comboKey);
  return (
    <div style={style} className={cn(...classes, 'left-cell')}>
      <div className='check-box-container'>
        <Checkbox
          className='checkbox'
          checked={comboKey in selectedRows}
          onChange={() => onSelectRow(comboKey)}
        />
      </div>
      {leftHeaderNames.map(segment => {
        const val = rowData[segment];
        const display =
          typeof val === 'object' && val !== null
            ? String((val as SegmentValue).value_name ?? '')
            : String(val ?? '');
        return (
          <div key={segment} className={cn('left-cell-div', segment)}>
            <span className='cell-text'>{display}</span>
          </div>
        );
      })}
    </div>
  );
};

interface NWCornerCellProps {
  selectAll: boolean;
  leftHeaderNames: string[];
  sortMap: Partial<Record<string, string>>;
  data: MappingRow[];
  setSelectedRows: Dispatch<SetStateAction<Record<string, MappingRow>>>;
  setSelectAll: (value: boolean) => void;
  onSort: (field: string, e: React.MouseEvent) => void;
}

const NWCornerCell = ({
  selectAll,
  leftHeaderNames,
  sortMap,
  data,
  setSelectedRows,
  setSelectAll,
  onSort
}: NWCornerCellProps) => (
  <div className='nw-corner'>
    <div className='check-box-container'>
      <Checkbox
        checked={selectAll}
        onChange={() => {
          if (selectAll) {
            setSelectedRows({});
          } else {
            const selectAllMap: Record<string, MappingRow> = {};
            for (const row of data) {
              selectAllMap[row.comboKey] = row;
            }
            setSelectedRows(current => ({ ...current, ...selectAllMap }));
          }
          setSelectAll(!selectAll);
        }}
      />
    </div>
    {leftHeaderNames.map(field => (
      <button
        key={field}
        type='button'
        className={cn('include-sort-header-caret', field, sortMap[field])}
        onClick={e => onSort(field, e)}
      >
        {field}
      </button>
    ))}
  </div>
);
